import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../shared/environments/environment.dev';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private keycloakInstance: Keycloak;
  // private readonly authChannelName = 'kavach-auth-channel';
  private readonly logoutEventType = 'LOGOUT';
  private readonly logoutEventStorageKey = 'pragathi-rail-auth-logout-event';
  private logoutChannel?: BroadcastChannel;
  private isLogoutInProgress = false;

  constructor() {
    this.keycloakInstance = new Keycloak({
      url: environment.KEYCLOAK_URLS.BASE_URL,
      realm: environment.REALM_NAME,
      clientId: environment.CLIENT_ID,
    });
    this.setupCrossTabLogoutListener();
  }

  init(): Promise<void> {
    return this.keycloakInstance
      .init({
        onLoad: 'login-required',
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (!authenticated) {
          localStorage.clear();
          return this.keycloakInstance.login({ redirectUri: environment.KEYCLOAK_URLS.REDIRECT_URL });
        }

        this.setupTokenMonitoring()

        return Promise.resolve();
      })
      .catch((error) => {
        console.error('Keycloak initialization failed', error);
        return Promise.reject(error);
      });
  }

  logout(notifyOtherTabs = true): void {
    if (this.isLogoutInProgress) {
      return;
    }

    this.isLogoutInProgress = true;

    if (notifyOtherTabs) {
      this.broadcastLogout();
    }

    localStorage.clear();
    sessionStorage.clear();
    this.keycloakInstance
      .logout({
        redirectUri: window.location.origin
      })
      .catch((error) => {
        console.error('Keycloak logout failed', error);
        this.isLogoutInProgress = false;
      });
  }

  clearToken(): void {
    if (this.keycloakInstance) {
      this.keycloakInstance.clearToken();
      console.log('Token cleared from Keycloak instance');
    } else {
      console.warn('Keycloak instance is not initialized');
    }
  }

  isLoggedIn(): boolean {
    return !!this.keycloakInstance && !!this.keycloakInstance.authenticated;
  }

  setupTokenMonitoring(): void {
    if (this.keycloakInstance) {
      this.keycloakInstance.onTokenExpired = () => {
        console.warn('Token expired, attempting to refresh');
        this.keycloakInstance
          .updateToken(30)
          .then((refreshed) => {
            if (!refreshed) {
              console.warn('Token not refreshed, logging out');
              this.logout();
            } else {
              console.log('Token refreshed successfully');
            }
          })
          .catch((error) => {
            console.error('Failed to refresh the token', error);
            this.logout();
          });
      };
    } else {
      console.warn('Keycloak instance is not initialized for token monitoring');
    }
  }

  getToken(): Promise<string> {
    const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    const isRefresh = isBrowser && (
      (window.performance?.navigation?.type === 1) ||
      (window.performance?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming)?.type === 'reload'
    );

    if (isRefresh && isBrowser) {
      const localToken = localStorage.getItem('token');
      if (localToken) {
        return Promise.resolve(localToken);
      }
    }

    if (!this.keycloakInstance) {
      return Promise.reject('Keycloak instance is not initialized');
    }

    return this.keycloakInstance
      .updateToken(30) 
      .then(() => {
        const token = this.keycloakInstance.token;
        if (!token) {
          return Promise.reject('Token is unavailable');
        }
        return token;
      })
      .catch((error) => {
        console.error('Failed to refresh the token', error);
        return Promise.reject('Failed to get a valid token');
      });
  }



  getKeycloakInstance(): Keycloak {
    return this.keycloakInstance;
  }

  private setupCrossTabLogoutListener(): void {
    if (!this.isBrowser()) {
      return;
    }

    // if ('BroadcastChannel' in window) {
    //   this.logoutChannel = new BroadcastChannel(this.authChannelName);
    //   this.logoutChannel.onmessage = (event: MessageEvent) => {
    //     if (this.isLogoutEvent(event.data)) {
    //       this.logout(false);
    //     }
    //   };
    // }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.logoutEventStorageKey && event.newValue) {
        this.logout(false);
      }
    });
  }

  private broadcastLogout(): void {
    const logoutEvent = {
      type: this.logoutEventType,
      timestamp: Date.now(),
    };

    this.logoutChannel?.postMessage(logoutEvent);
    localStorage.setItem(this.logoutEventStorageKey, JSON.stringify(logoutEvent));
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined' && typeof sessionStorage !== 'undefined';
  }

  private isLogoutEvent(eventData: unknown): eventData is { type: string; timestamp: number } {
    return !!eventData && typeof eventData === 'object' && (eventData as { type?: string }).type === this.logoutEventType;
  }
}
